import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Box, CircularProgress, Typography,
  Stack
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import {
  addKumes, getIller, getIlceler, getBolgelerForKumesForm,
  getVeterinerlerForKumesForm, getYemFabrikalariForKumesForm, getHayvanCinsleriForKumesForm,
  getOluHayvanImhaTipleri
} from '../services/api'; 


const orangeTheme = createTheme({
    palette: { primary: { main: '#FF7A2B' } },
    components: {
      MuiOutlinedInput: { styleOverrides: { root: { '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF7A2B' } } } },
      MuiInputLabel: { styleOverrides: { root: { '&.Mui-focused': { color: '#FF7A2B' } } } },
    },
});

const KumesModal = ({ open, handleClose, onSave, sirketId }) => {
  const [formData, setFormData] = useState({});
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [bolgeler, setBolgeler] = useState([]);
  const [veterinerler, setVeterinerler] = useState([]);
  const [yemFabrikalari, setYemFabrikalari] = useState([]);
  const [hayvanCinsleri, setHayvanCinsleri] = useState([]);
  const [imhaTipleri, setImhaTipleri] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchDropdownData = async () => {
      if (!open) return;
      if (!sirketId) {
        enqueueSnackbar('Lütfen yeni kümes eklemek için önce bir şirket seçin.', { variant: 'warning' });
        return;
      }
      setLoading(true);
      setFormData({}); 
      setIlceler([]);   

      try {
        const [illerRes, vetRes, yemRes, bolgeRes, hayvanRes, imhaRes] = await Promise.all([
          getIller(),
          getVeterinerlerForKumesForm(sirketId),
          getYemFabrikalariForKumesForm(sirketId),
          getBolgelerForKumesForm(sirketId),
          getHayvanCinsleriForKumesForm(),
          getOluHayvanImhaTipleri()
        ]);

        setIller(illerRes.data?.data || []);
        setVeterinerler(vetRes.data?.data || []);
        setYemFabrikalari(yemRes.data?.data || []);
        setBolgeler(bolgeRes.data?.data || []);
        setHayvanCinsleri(hayvanRes.data?.data || []);
        setImhaTipleri(imhaRes.data?.data || []);

      } catch (err) {
        enqueueSnackbar(`Tanımlama verileri yüklenirken hata oluştu: ${err.message}`, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchDropdownData();
  }, [open, sirketId, enqueueSnackbar]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleIlChange = async (e) => {
    const ilId = e.target.value;
    setFormData({ ...formData, IL_ID: ilId, ILCE_ID: '' });
    setIlceler([]);
    if (ilId) {
      setLoading(true);
      try {
        const ilceRes = await getIlceler(ilId);
        setIlceler(ilceRes.data?.data || []);
      } catch (err) {
        enqueueSnackbar(`İlçeler yüklenirken hata oluştu: ${err.message}`, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

const handleSubmit = async () => {
  setLoading(true);
  try {
    const dataToSend = {
      ...formData,
      ENTEGRE_SIRKET_ID: sirketId,
    };

    await addKumes(dataToSend);
    enqueueSnackbar('Yeni kümes başarıyla eklendi.', { variant: 'success' });
    onSave();
    handleClose();
  } catch (err) {
    const errorMessage =
      err.response?.data?.message ||
      err.response?.data ||
      err.message;

    enqueueSnackbar(`İşlem sırasında hata: ${errorMessage}`, { variant: 'error' });
  } finally {
    setLoading(false);
  }
};

  return (
    <ThemeProvider theme={orangeTheme}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Kümes Ekle</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Veriler Yükleniyor...</Typography>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
                              <TextField
                  fullWidth
                  label="Kümes Adı"
                  name="KUMES_ADI"
                  value={formData.KUMES_ADI || ''}
                  onChange={handleChange}
                />
              <FormControl fullWidth><InputLabel>Bölge</InputLabel><Select name="BOLGE_ID" value={formData.BOLGE_ID || ''} label="Bölge" onChange={handleChange}>{bolgeler.map(b => <MenuItem key={b.BOLGE_ID} value={b.BOLGE_ID}>{b.BOLGE_ADI}</MenuItem>)}</Select></FormControl>
              <FormControl fullWidth><InputLabel>Veteriner</InputLabel><Select name="VETERINER_ID" value={formData.VETERINER_ID || ''} label="Veteriner" onChange={handleChange}>{veterinerler.map(v => <MenuItem key={v.VETERINER_ID} value={v.VETERINER_ID}>{v.VETERINER_ADI}</MenuItem>)}</Select></FormControl>
              <FormControl fullWidth><InputLabel>Yem Fabrikası</InputLabel><Select name="YEM_FABRIKA_ID" value={formData.YEM_FABRIKA_ID || ''} label="Yem Fabrikası" onChange={handleChange}>{yemFabrikalari.map(y => <MenuItem key={y.YEM_ID} value={y.YEM_ID}>{y.YEM_FABRIKA_ADI}</MenuItem>)}</Select></FormControl>
              <FormControl fullWidth><InputLabel>Hayvan Cinsi</InputLabel><Select name="HAYVAN_CINS_ID" value={formData.HAYVAN_CINS_ID || ''} label="Hayvan Cinsi" onChange={handleChange}>{hayvanCinsleri.map(c => <MenuItem key={c.HAYVAN_CINS_ID} value={c.HAYVAN_CINS_ID}>{c.HAYVAN_TANIM_ADI}</MenuItem>)}</Select></FormControl>
              <FormControl fullWidth><InputLabel>Ölen Hayvan İmha Tipi</InputLabel><Select name="OLU_HAYVAN_IMHA_TIP_ID" value={formData.OLU_HAYVAN_IMHA_TIP_ID || ''} label="Ölen Hayvan İmha Tipi" onChange={handleChange}>{imhaTipleri.map(t => <MenuItem key={t.OLU_HAYVAN_IMHA_TIP_ID} value={t.OLU_HAYVAN_IMHA_TIP_ID}>{t.OLU_HAYVAN_IMHA_TIP_ADI}</MenuItem>)}</Select></FormControl>
              
              <TextField fullWidth label="Adres" name="ADRES" value={formData.ADRES || ''} onChange={handleChange} multiline rows={2} />
              <FormControl fullWidth ><InputLabel>İl</InputLabel><Select name="IL_ID" value={formData.IL_ID || ''} label="İl" onChange={handleIlChange}>{iller.map(il => <MenuItem key={il.IL_ID} value={il.IL_ID}>{il.IL_ADI}</MenuItem>)}</Select></FormControl>
              <FormControl fullWidth  disabled={!formData.IL_ID || ilceler.length === 0}><InputLabel>İlçe</InputLabel><Select name="ILCE_ID" value={formData.ILCE_ID || ''} label="İlçe" onChange={handleChange}>{ilceler.map(ilce => <MenuItem key={ilce.ILCE_ID} value={ilce.ILCE_ID}>{ilce.ILCE_ADI}</MenuItem>)}</Select></FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: '16px' }}>
          <Button onClick={handleClose}>İptal</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>{loading ? <CircularProgress size={24} color="inherit"/> : 'Ekle'}</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default KumesModal;